import React, { useEffect, useState } from 'react';
import axios from '../config/api';
import { API_URL } from '../config/api';
import { MdOpenInNew, MdNewspaper } from 'react-icons/md';

/**
 * Generic "Related News" block backed by Google News RSS.
 * Lazy-loads on first render. Hides itself when the feed returns nothing
 * so empty placeholders don't clutter the UI.
 *
 * Props:
 *   - endpoint: '/api/news/bill' | '/api/news/poll/<id>' | '/api/news/homepage'
 *   - params: optional query params for the endpoint
 *   - limit: number of items to fetch (default 4)
 *   - title: section header (default "Related News")
 *   - testIdPrefix: data-testid prefix
 *   - compact: dense rendering for poll cards (default false)
 */
function RelatedNews({
  endpoint,
  params = {},
  limit = 4,
  title = 'Related News',
  testIdPrefix = 'related-news',
  compact = false,
  hideWhenEmpty = false,
  wrapperClassName = '',
}) {
  const [items, setItems] = useState(null); // null = loading, [] = no results
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}${endpoint}`, {
          params: { ...params, limit },
        });
        if (!cancelled) setItems(res.data?.items || []);
      } catch (e) {
        if (!cancelled) {
          setError(true);
          setItems([]);
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, JSON.stringify(params), limit]);

  if (items === null) {
    if (hideWhenEmpty) return null;
    return (
      <div className={wrapperClassName}>
        <div className="text-xs text-gray-400 italic" data-testid={`${testIdPrefix}-loading`}>
          Fetching related news…
        </div>
      </div>
    );
  }
  if (error || items.length === 0) {
    if (hideWhenEmpty) return null;
    return (
      <div className={wrapperClassName}>
        <div className="text-xs text-gray-400 italic" data-testid={`${testIdPrefix}-empty`}>
          No related news found right now.
        </div>
      </div>
    );
  }

  const inner = (
    <div className="space-y-2" data-testid={`${testIdPrefix}-list`}>
      <div className="flex items-center gap-2 text-gray-500 font-rubik font-semibold text-xs uppercase tracking-wide">
        <MdNewspaper className="text-sm" />
        {title}
      </div>
      <ul className={compact ? 'space-y-1' : 'space-y-2'}>
        {items.map((it, idx) => (
          <li key={`${it.link}-${idx}`}>
            <a
              href={it.link}
              target="_blank"
              rel="noopener noreferrer"
              className={`group flex items-start gap-2 ${compact ? 'p-2' : 'p-2.5'} rounded-md border border-gray-100 bg-white hover:border-sky-200 hover:bg-sky-50/40 transition-colors`}
              data-testid={`${testIdPrefix}-item-${idx}`}
            >
              <span className="flex-1 min-w-0">
                <span className={`block ${compact ? 'text-xs' : 'text-sm'} font-rubik font-medium text-gray-800 group-hover:text-sky-700 line-clamp-2`}>
                  {it.title}
                </span>
                {(it.source || it.published) && (
                  <span className="block mt-1 text-[10px] text-gray-500">
                    {it.source}
                    {it.source && it.published ? ' · ' : ''}
                    {it.published ? new Date(it.published).toLocaleDateString() : ''}
                  </span>
                )}
              </span>
              <MdOpenInNew className="shrink-0 mt-0.5 text-sm text-gray-300 group-hover:text-sky-500" />
            </a>
          </li>
        ))}
      </ul>
      <div className="text-[10px] text-gray-400 italic">
        Headlines via Google News.
      </div>
    </div>
  );

  return wrapperClassName ? <div className={wrapperClassName}>{inner}</div> : inner;
}

export default RelatedNews;
